import React, { useState } from 'react';
import { Modal, Form, Radio, Input, Divider, Typography, Space, Image, Alert, notification } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, UserOutlined } from '@ant-design/icons';
import { hazardService } from '@iso/services/hazard.service';
import { commonHelper } from '@iso/lib/helpers/commonHelper';

const { TextArea } = Input;
const { Text, Title } = Typography;

/**
 * Confirm Immediate Resolution Modal
 * Manager reviews and approves/rejects reporter's claim of immediate resolution
 */
const ConfirmImmediateResolutionModal = ({
  visible,
  hazard,
  workflowState,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState(null);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const approved = values.decision === 'approve';

      const result = await hazardService.confirmImmediateResolution(
        hazard.id,
        approved,
        values.comment
      );

      if (result && result.code === '0000') {
        notification.success({
          message: 'Success',
          description: approved
            ? 'Immediate resolution approved. Hazard is now resolved.'
            : 'Immediate resolution rejected. Hazard returned to normal workflow.',
          icon: approved
            ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
            : <CloseCircleOutlined style={{ color: '#f5222d' }} />
        });
        form.resetFields();
        setDecision(null);
        if (onSuccess) onSuccess();
      } else {
        notification.error({
          message: 'Error',
          description: result.message || 'Failed to process confirmation',
        });
      }
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setDecision(null);
    if (onCancel) onCancel();
  };

  if (!hazard || !workflowState) {
    return null;
  }

  const reporter = hazard.creator;
  const resolutionNotes = workflowState.resolution_notes;
  const resolutionPhotos = hazard.images?.filter(img => img.photo_context === 'resolution') || [];

  return (
    <Modal
      title="Confirm Immediate Resolution"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText={decision === 'approve' ? 'Approve Resolution' : decision === 'reject' ? 'Reject & Start Workflow' : 'Confirm'}
      okButtonProps={{
        disabled: !decision,
        icon: decision === 'approve' ? <CheckCircleOutlined /> : <CloseCircleOutlined />,
        danger: decision === 'reject'
      }}
      width={700}
      destroyOnClose
    >
      <Alert
        message="Manager Action Required"
        description="The reporter claims they resolved this hazard immediately. Please review the resolution details and approve or reject."
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Hazard Information */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>{hazard.title}</Title>
        <Text type="secondary">{hazard.description}</Text>
      </div>

      <Divider orientation="left">Reporter Information</Divider>
      <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 16 }}>
        <Space>
          <UserOutlined />
          <Text strong>{reporter?.name || 'Unknown'}</Text>
          {reporter?.email && <Text type="secondary">({reporter.email})</Text>}
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Reported: {commonHelper.displayDateTimeLocal(hazard.created_at)}
        </Text>
      </Space>

      <Divider orientation="left">Resolution Details</Divider>
      <div style={{ marginBottom: 16 }}>
        <Text strong>Resolution Notes:</Text>
        <div style={{
          marginTop: 8,
          padding: 12,
          background: '#f5f5f5',
          borderRadius: 4,
          whiteSpace: 'pre-wrap'
        }}>
          <Text>{resolutionNotes || 'No notes provided'}</Text>
        </div>
      </div>

      {resolutionPhotos.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Resolution Photos ({resolutionPhotos.length}):</Text>
          <div style={{ marginTop: 8 }}>
            <Image.PreviewGroup>
              <Space wrap>
                {resolutionPhotos.map((photo, index) => (
                  <Image
                    key={photo.id || index}
                    width={100}
                    height={100}
                    src={photo.url || photo.path}
                    alt={`Resolution photo ${index + 1}`}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                  />
                ))}
              </Space>
            </Image.PreviewGroup>
          </div>
        </div>
      )}

      <Divider orientation="left">Your Decision</Divider>

      <Form
        form={form}
        layout="vertical"
        preserve={false}
        onValuesChange={(changedValues) => {
          if (changedValues.decision) {
            setDecision(changedValues.decision);
          }
        }}
      >
        <Form.Item
          name="decision"
          label="Decision"
          rules={[
            {
              required: true,
              message: 'Please make a decision'
            }
          ]}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="approve">
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span><strong>Approve</strong> - Resolution is satisfactory. Close this hazard.</span>
                </Space>
              </Radio>
              <Radio value="reject">
                <Space>
                  <CloseCircleOutlined style={{ color: '#f5222d' }} />
                  <span><strong>Reject</strong> - Resolution is not satisfactory. Start normal escalation workflow.</span>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="comment"
          label="Manager Comment (Optional)"
          extra="Provide feedback to the reporter about your decision"
        >
          <TextArea
            rows={3}
            placeholder="Add any comments or feedback..."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>

      {decision === 'reject' && (
        <Alert
          message="Rejection Impact"
          description="Rejecting will change the hazard status to 'In Progress' and assign it to the reporter's direct manager (Level 1) with a 2 business day deadline."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default ConfirmImmediateResolutionModal;
